import axios from 'axios';
import { API, BACKEND_URL } from '../constants/api';
import { toast } from 'sonner';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '../utils/authTokenStorage';
import { ErrorHandler } from '../utils/errorHandler';

const GET_CACHE_TTL_MS = 90 * 1000; // 90 seconds
const GET_CACHE_MAX_ENTRIES = 100;  // cap size to avoid unbounded memory use

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000, // 10 second timeout
      withCredentials: true, // Enable cookies (httpOnly cookies for auth)
    });

    // Short-lived cache for GET requests to avoid duplicate identical requests
    this._getCache = new Map();
    
    // Flag to prevent multiple simultaneous refresh attempts
    this.isRefreshing = false;
    this.refreshPromise = null;
    
    // Add request interceptor for CSRF token, Bearer token (when cookies not sent), and optional endpoints
    this.client.interceptors.request.use(
      (config) => {
        const url = config.url || '';
        const accessToken = getAccessToken();
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        // Get CSRF token from cookie and add to header for state-changing requests
        // CSRF token is in a non-httpOnly cookie, readable by JavaScript
        if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase())) {
          const csrfToken = this.getCsrfTokenFromCookie();
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }
        // Mark endpoints that may return 401 as expected (suppress console errors)
        const isAuthPage = window.location.pathname === '/auth' || window.location.pathname.startsWith('/auth/');
        if (url.includes('/strava/') || url.includes('/my-connections') || (url.includes('/auth/me') && isAuthPage)) {
          config._suppress401Console = true;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    // Add response interceptor for error handling and cache invalidation
    const self = this;
    this.client.interceptors.response.use(
      (response) => {
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(response.config?.method?.toUpperCase())) {
          self.clearGetCache();
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        
        // Don't log errors for requests that use validateStatus (they handle errors themselves)
        if (originalRequest?.validateStatus && typeof originalRequest.validateStatus === 'function') {
          // This request has custom status validation, don't log it
          return Promise.reject(error);
        }
        
        // Handle common errors
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          const isOptionalEndpoint = url.includes('/strava/') || url.includes('/my-connections');
          const isAuthPage = window.location.pathname === '/auth' || window.location.pathname.startsWith('/auth/');
          const isAuthMeEndpoint = url.includes('/auth/me');
          const isRefreshEndpoint = url.includes('/auth/refresh');
          
          // For optional endpoints (like Strava), don't try to refresh - just return error gracefully
          if (isOptionalEndpoint) {
            return Promise.reject(error);
          }
          
          // Don't try to refresh if refresh endpoint itself failed
          if (isRefreshEndpoint) {
            await this.handleTokenExpiration();
            return Promise.reject(error);
          }
          
          // For /auth/me, try to refresh if not on auth page (401 on auth page = not logged in)
          if (isAuthMeEndpoint && !isAuthPage && !originalRequest._retry && !this.isRefreshing) {
            originalRequest._retry = true;
            originalRequest._isSessionCheck = true;
            this.isRefreshing = true;
            try {
              const refreshToken = getRefreshToken();
              this.refreshPromise = this.client.post(
                '/auth/refresh',
                refreshToken ? { refresh_token: refreshToken } : undefined
              );
              const refreshResponse = await this.refreshPromise;
              if (refreshResponse?.data?.access_token) {
                setTokens(refreshResponse.data.access_token, refreshResponse.data.refresh_token);
              }
              this.isRefreshing = false;
              this.refreshPromise = null;
              
              // Retry the original request - reconstruct the request properly
              const retryConfig = {
                method: originalRequest.method || 'get',
                url: originalRequest.url,
                params: originalRequest.params,
                headers: originalRequest.headers,
                _isSessionCheck: true // Mark retry as session check too
              };
              
              return this.client.request(retryConfig);
            } catch (refreshError) {
              // Refresh failed - user is not authenticated
              this.isRefreshing = false;
              this.refreshPromise = null;
              
              // Mark error as session check to prevent toast
              error._isSessionCheck = true;
              
              // Return the original error (401 from /auth/me) - don't redirect or show toast
              return Promise.reject(error);
            }
          } else if (this.isRefreshing && this.refreshPromise) {
            // If we're already refreshing, wait for that promise
            try {
              await this.refreshPromise;
              this.isRefreshing = false;
              this.refreshPromise = null;
              
              // Mark retry as session check if original was
              const isSessionCheck = originalRequest._isSessionCheck || false;
              
              // Retry the original request - reconstruct properly
              const retryConfig = {
                method: originalRequest.method || 'get',
                url: originalRequest.url,
                params: originalRequest.params,
                headers: originalRequest.headers,
                _isSessionCheck: isSessionCheck
              };
              
              return this.client.request(retryConfig);
            } catch (refreshError) {
              this.isRefreshing = false;
              this.refreshPromise = null;
              
              // Only redirect if not a session check
              if (!originalRequest._isSessionCheck) {
                await this.handleTokenExpiration();
              } else {
                // Mark error as session check to prevent toast
                error._isSessionCheck = true;
              }
              return Promise.reject(error);
            }
          } else {
            // Other 401 errors - only redirect if not on auth page and not a session check
            if (!isAuthPage && !originalRequest._isSessionCheck) {
              await this.handleTokenExpiration();
            } else if (originalRequest._isSessionCheck) {
              // Mark error as session check to prevent toast
              error._isSessionCheck = true;
            }
            return Promise.reject(error);
          }
        }
        
        // Handle CSRF token errors
        if (error.response?.status === 403 && error.response?.data?.detail?.includes('CSRF')) {
          toast.error('Security error. Please refresh the page and try again.');
          // Optionally refresh the page to get a new CSRF token
          setTimeout(() => window.location.reload(), 2000);
          return Promise.reject(error);
        }
        
        // Handle network errors (show toast once to avoid spam; backend unreachable is the usual cause)
        if (!error.response) {
          const isNetworkError = error.code === 'ECONNREFUSED' ||
            error.message === 'Network Error' ||
            error.code === 'ERR_NETWORK';
          if (!self._lastNetworkErrorToast || Date.now() - self._lastNetworkErrorToast > 5000) {
            self._lastNetworkErrorToast = Date.now();
            if (isNetworkError) {
              toast.error('Cannot reach the server. Is the backend running? (e.g. python server.py on port 8000)');
            } else {
              toast.error('Unable to connect. Check your internet connection.');
            }
          }
        }
        
        return Promise.reject(error);
      }
    );
  }
  
  // Helper to get CSRF token from cookie
  getCsrfTokenFromCookie() {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  // Health check method
  async checkHealth() {
    const healthUrl = `${BACKEND_URL}/health`;
    const response = await axios.get(healthUrl);
    return response.data;
  }

  // Handle token expiration (redirect to login)
  async handleTokenExpiration() {
    try {
      clearTokens();
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      window.location.href = '/auth';
    } catch (error) {
      clearTokens();
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    }
  }

  /**
   * Build a stable cache key for GET requests (param key order independent).
   * Omits undefined values so optional params do not create separate cache entries.
   */
  _getCacheKey(endpoint, params) {
    const p = params && typeof params === 'object' && !Array.isArray(params) ? params : {};
    const sorted = Object.keys(p).sort();
    const canonical = {};
    for (const k of sorted) {
      if (p[k] !== undefined) canonical[k] = p[k];
    }
    return `${endpoint}:${JSON.stringify(canonical)}`;
  }

  /**
   * Evict oldest GET cache entries until size is at or below max.
   */
  _evictGetCacheIfNeeded() {
    if (this._getCache.size <= GET_CACHE_MAX_ENTRIES) return;
    const entries = Array.from(this._getCache.entries(), ([key, val]) => ({ key, at: val.at }));
    entries.sort((a, b) => a.at - b.at);
    const toRemove = entries.slice(0, this._getCache.size - GET_CACHE_MAX_ENTRIES);
    for (const { key } of toRemove) this._getCache.delete(key);
  }

  // Generic HTTP methods
  async get(endpoint, params = {}, config = {}) {
    const useCache = config.cache !== false && !config.validateStatus && !config.suppress404Error;
    const cacheKey = useCache ? this._getCacheKey(endpoint, params) : null;
    if (cacheKey && this._getCache.has(cacheKey)) {
      const entry = this._getCache.get(cacheKey);
      if (Date.now() - entry.at < GET_CACHE_TTL_MS) return entry.data;
      this._getCache.delete(cacheKey);
    }
    try {
      const response = await this.client.get(endpoint, { params, ...config });
      const data = response.data;
      if (cacheKey) {
        this._getCache.set(cacheKey, { data, at: Date.now() });
        this._evictGetCacheIfNeeded();
      }
      return data;
    } catch (error) {
      // Don't handle 404 errors for certain endpoints (like meeting lookups)
      // where 404s are expected and should be handled by the caller
      if (error.response?.status === 404 && config.suppress404Error) {
        throw error; // Re-throw without logging
      }
      this.handleError(error, 'GET', endpoint);
      throw error;
    }
  }

  /** Clear GET cache (e.g. after mutations so list views show fresh data). */
  clearGetCache() {
    this._getCache.clear();
  }

  async put(endpoint, data = {}) {
    try {
      const response = await this.client.put(endpoint, data);
      return response.data;
    } catch (error) {
      this.handleError(error, 'PUT', endpoint);
      throw error;
    }
  }

  async post(endpoint, data = {}, config = {}) {
    try {
      const response = await this.client.post(endpoint, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error, 'POST', endpoint);
      throw error;
    }
  }

  async patch(endpoint, data = {}) {
    try {
      const response = await this.client.patch(endpoint, data);
      return response.data;
    } catch (error) {
      this.handleError(error, 'PATCH', endpoint);
      throw error;
    }
  }

  async delete(endpoint, config = {}) {
    try {
      const response = await this.client.delete(endpoint, config);
      return response.data;
    } catch (error) {
      this.handleError(error, 'DELETE', endpoint);
      throw error;
    }
  }

  // Specific API methods for common operations
  auth = {
    login: (emailOrPhone, password, userType) => 
      this.post('/auth/login', { emailOrPhone, password, user_type: userType }),
    
    register: (userData) => 
      this.post('/auth/register', userData),
    
    google: (idToken, userType) =>
      this.post('/auth/google', { id_token: idToken, user_type: userType }),
    
    getCurrentUser: async () => {
      // Don't use validateStatus - let the interceptor handle token refresh
      // The interceptor will try to refresh the token if it's expired
      // Only return null if refresh also fails (user truly not authenticated)
      try {
        const response = await this.client.get('/auth/me');
        return response.data;
      } catch (error) {
        // If it's a 401 after refresh attempt, user is not authenticated - return null
        // The interceptor will have already tried to refresh the token
        if (error.response?.status === 401) {
          return null;
        }
        throw error;
      }
    },
    
    logout: async () => {
      try {
        // Stop any ongoing refresh attempts
        this.isRefreshing = false;
        this.refreshPromise = null;
        
        // Call logout endpoint to clear cookies on server
        await this.post('/auth/logout');
      } catch (error) {
        ErrorHandler.logError('apiService.auth.logout', error);
      } finally {
        clearTokens();
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    },
    
    forgotPassword: (email, userType) => 
      this.post('/auth/forgot-password', { email, user_type: userType }),
    
    verifyResetToken: async (token) => {
      const response = await this.client.post(`/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
      return response.data;
    },
    
    resetPassword: (token, newPassword) => 
      this.post('/auth/reset-password', { token, new_password: newPassword })
  };

  users = {
    /**
     * Get user registrations with pagination
     * @param {Object} params - Query parameters (skip, limit)
     * @returns {Promise<Array>} User registrations
     */
    getRegistrations: (params = {}) => {
      const queryParams = {
        skip: params.skip || 0,
        limit: params.limit || 50,
      };
      return this.get('/users/me/registrations', { params: queryParams });
    },
    
    cancelRegistration: (registrationId, cancelAllFuture = false) => 
      this.delete(`/users/me/registrations/${registrationId}${cancelAllFuture ? '?cancel_all_future=true' : ''}`),
    
    updateLocation: (locationData) => 
      this.put('/auth/me/location', locationData),
    
    changePassword: (passwordData) => 
      this.put('/auth/me/password', passwordData),
    
    updatePhone: (phoneData) => 
      this.put('/auth/me/phone', phoneData),
    
    updateEmail: (emailData) => 
      this.put('/auth/me/email', emailData),
    
    updateProfilePicture: (pictureData) => 
      this.put('/auth/me/profile-picture', pictureData),
    
    /**
     * @deprecated Hero image is stored in runcards collection only. Use runcards.updateMe({ image }) instead.
     */
    updateRunCardImage: (imageData) => 
      this.put('/auth/me/run-card-image', imageData),
    
    updateProfile: (profileData) => 
      this.put('/auth/me', profileData),
    
    getProfile: (userId) => 
      this.get(`/users/${userId}/profile`)
  };

  /** Hero image and runcard data live in runcards collection; use these for create/update. */
  runcards = {
    getMe: () => this.get('/runcards/me'),
    updateMe: (data) => this.put('/runcards/me', data),
  };

  clubs = {
    getAll: (params = {}) => 
      this.get('/clubs', params),
    
    getById: (clubId) => 
      this.get(`/clubs/${clubId}`),
    
    create: (clubData) => 
      this.post('/clubs', clubData),
    
    update: (clubId, clubData) => 
      this.put(`/clubs/${clubId}`, clubData),
    
    getStats: (clubId) => 
      this.get(`/clubs/${clubId}/stats`),
    
    getProfile: (clubId) => 
      this.get(`/clubs/${clubId}/profile`),
    
    leaveClub: (clubId) => 
      this.delete(`/clubs/${clubId}/leave`),
    
    getClubConnections: (clubId, limit = 50) => 
      this.get(`/clubs/${clubId}/connections`, { params: { limit } }),
    
    getClubMembers: (clubId, limit = 50, offset = 0) => {
      if (!clubId) {
        return Promise.reject(new Error('Club ID is required'));
      }
      return this.get(`/clubs/${clubId}/club_members`, { params: { limit, offset } });
    },
    
    getConnectionStatus: (clubId) => 
      this.get(`/clubs/${clubId}/connection-status`),
    
    getMyConnections: async () => {
      try {
        const response = await this.client.get('/clubs/my-connections', {
          validateStatus: (status) => status < 500 // Don't treat 401 as error
        });
        if (response.status === 401) {
          return [];
        }
        return response.data;
      } catch (error) {
        // If user doesn't have connections or not authenticated, return empty array
        // 401 means not authenticated
        if (error.response?.status === 401) {
          return [];
        }
        throw error;
      }
    },
    
    // Club Connection Methods
    connectClub: (clubId) => 
      this.post(`/clubs/${clubId}/connect`),
    
    disconnectClub: (clubId) => 
      this.delete(`/clubs/${clubId}/disconnect`),
    
    joinRun: (clubId, registrationData) => 
      this.post(`/clubs/${clubId}/join`, registrationData),
    
    getRunParticipants: (clubId, meetingIndex) => 
      this.get(`/clubs/${clubId}/runs/${meetingIndex}/participants`),

    // Club group chat
    getClubChat: (clubId) =>
      this.get(`/clubs/${clubId}/chat`),
    getClubChatMessages: (clubId, params = {}) =>
      this.get(`/clubs/${clubId}/chat/messages`, { params }),
    sendClubChatMessage: (clubId, content) =>
      this.post(`/clubs/${clubId}/chat/messages`, { content }),
    markClubChatRead: (clubId) =>
      this.patch(`/clubs/${clubId}/chat/read`)
  };

  clubChats = {
    list: () =>
      this.get('/chat/club-chats')
  };

  clubRatings = {
    createRating: (ratingData) => 
      this.post(`/clubs/${ratingData.club_id}/ratings`, ratingData),
    
    updateRating: (ratingId, ratingData) => 
      this.put(`/ratings/${ratingId}`, ratingData),
    
    deleteRating: (ratingId) => 
      this.delete(`/ratings/${ratingId}`),
    
    getClubRatings: (clubId, limit) => 
      this.get(`/clubs/${clubId}/ratings`, { params: { limit } }),
    
    getClubRatingStats: (clubId) => 
      this.get(`/clubs/${clubId}/ratings/stats`),
    
    getMyClubRating: (clubId) => 
      this.get(`/clubs/${clubId}/ratings/my-rating`),
    
    canRateClub: (clubId) => 
      this.get(`/clubs/${clubId}/ratings/can-rate`),
    
    getUserRatings: (userId, limit) => 
      this.get(`/users/${userId}/ratings`, { params: { limit } }),
    
    getRating: (ratingId) => 
      this.get(`/ratings/${ratingId}`)
  };

  meetings = {
    getAll: (params = {}, location = null) => {
      const queryParams = { ...params };
      if (location && typeof location.latitude === 'number' && typeof location.longitude === 'number') {
        queryParams.user_lat = location.latitude;
        queryParams.user_lon = location.longitude;
      }
      return this.get('/meetings/list', queryParams);
    },
    
    getById: (meetingId, suppress404 = false) => 
      this.get(`/meetings/${meetingId}`, {}, { suppress404Error: suppress404, validateStatus: (status) => status < 500 }),
    
    create: (meetingData) => 
      this.post('/meetings', meetingData),
    
    update: (meetingId, meetingData) => 
      this.put(`/meetings/${meetingId}`, meetingData),
    
    delete: (meetingId, deleteEntireSeries = false) => 
      this.delete(`/meetings/${meetingId}`, { params: { delete_entire_series: deleteEntireSeries } }),
    
    cancel: (meetingId, reason = null, cancelEntireSeries = false) => {
      const params = {};
      if (reason) params.reason = reason;
      if (cancelEntireSeries) params.cancel_entire_series = cancelEntireSeries;
      return this.post(`/meetings/${meetingId}/cancel`, null, { params });
    },
    
    save: (meetingId, runclubId = null) => 
      this.post(`/meetings/${meetingId}/save${runclubId ? `?runclub_id=${encodeURIComponent(runclubId)}` : ''}`),
    
    unsave: (meetingId) => 
      this.delete(`/meetings/${meetingId}/save`),
    
    getSavedRuns: (meetingId) => 
      this.get(`/meetings/${meetingId}/saved-runs`),
    
    getMySavedRuns: () => 
      this.get('/me/saved-runs')
  };

  strava = {
    getStatus: async () => {
      try {
        const response = await this.client.get('/strava/status', {
          validateStatus: (status) => status < 500 // Don't treat 401 as error
        });
        if (response.status === 401) {
          return null;
        }
        return response.data;
      } catch (error) {
        // If user doesn't have Strava connection, return null
        // 401 means not authenticated/connected
        if (error.response?.status === 401) {
          return null;
        }
        throw error;
      }
    },
    
    connect: (code) => 
      this.post('/strava/connect', { 
        code: code,
        scope: 'read,activity:read_all,profile:read_all',
        mobile: false
      }),
    
    disconnect: () => 
      this.delete('/strava/disconnect'),
    
    getActivities: async () => {
      try {
        const response = await this.client.get('/strava/activities', {
          validateStatus: (status) => status < 500 // Don't treat 401 as error
        });
        if (response.status === 401 || response.status === 400) {
          return [];
        }
        return response.data;
      } catch (error) {
        // If user doesn't have Strava connection, return empty array
        // 401 means not authenticated/connected, 400 means no connection
        if (error.response?.status === 400 || error.response?.status === 401) {
          return [];
        }
        throw error;
      }
    },
    
    sync: (limit = 200) => 
      this.post('/strava/sync', { limit }),
    
    getStats: async () => {
      try {
        const response = await this.client.get('/strava/stats', {
          validateStatus: (status) => status < 500 // Don't treat 401 as error
        });
        if (response.status === 401 || response.status === 400) {
          return {
            total_activities: 0,
            total_distance: 0,
            total_time: 0,
            total_elevation_gain: 0,
            average_speed: 0,
            recent_activities: [],
            personal_records: {},
            weekly_stats: {},
            monthly_stats: {}
          };
        }
        return response.data;
      } catch (error) {
        // If user doesn't have Strava connection, return empty stats
        // 401 means not authenticated/connected, 400 means no connection
        if (error.response?.status === 400 || error.response?.status === 401) {
          return {
            total_activities: 0,
            total_distance: 0,
            total_time: 0,
            total_elevation_gain: 0,
            average_speed: 0,
            recent_activities: [],
            personal_records: {},
            weekly_stats: {},
            monthly_stats: {}
          };
        }
        throw error;
      }
    },
    
    getRecent: (days = 30) => 
      this.get('/strava/recent', { params: { days } }),
    
    getAuthorizationUrl: () => 
      this.get('/strava/authorize')
  };

  upload = {
    image: (file, category = 'profile') => {
      const formData = new FormData();
      formData.append('file', file);
      
      // Mobile-specific timeout (longer for mobile networks)
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const timeout = isMobile ? 120000 : 60000; // 2 minutes for mobile, 1 minute for desktop
      
      return this.client.post(`/upload/image?category=${category}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: timeout,
      });
    },
    
    uploadImage: (formData) => {
      return this.client.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    
    deleteImage: (imageUrl) => {
      return this.client.delete(`/upload/image?image_url=${encodeURIComponent(imageUrl)}`);
    }
  };

  // Combined data fetching for homepage (single request, smaller payload)
  getHomePageData = async (params = {}) => {
    const response = await this.get('/homepage', params);
    return {
      meetings: response?.meetings ?? [],
      clubs: response?.clubs ?? []
    };
  };

  // Error handling helper
  handleError(error, method, endpoint) {
    // Don't show error toast for session checks (expected 401s when checking if user is logged in)
    if (error._isSessionCheck || error.config?._isSessionCheck) {
      return;
    }
    
    if (error.response) {
      const status = error.response.status;
      const detail = error.response.data?.detail || '';
      
      // Handle Pydantic validation errors (422 status with array of errors)
      let errorMessage = '';
      if (status === 422 && Array.isArray(detail)) {
        const firstError = detail[0];
        if (firstError && firstError.msg) {
          // Map specific validation errors to user-friendly messages
          const errorMappings = {
            'String should have at least 8 characters': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
            'String should have at least 1 character': 'This field cannot be empty',
            'value is not a valid email address: An email address must have an @-sign.': 'Please enter a valid email address',
            'value is not a valid email address': 'Please enter a valid email address',
            'Invalid phone number. Enter a valid phone number with country code (e.g. +1 for US).': 'Enter a valid phone number with country code (e.g. +1 for US)',
            'Value error, Invalid phone number. Enter a valid phone number with country code (e.g. +1 for US).': 'Enter a valid phone number with country code (e.g. +1 for US)',
            'Password must contain at least one uppercase letter': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
            'Password must contain at least one lowercase letter': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
            'Password must contain at least one digit': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
            "Input should be 'runner' or 'runclub'": 'Please select a valid user type',
            'Name cannot be empty': 'Name is required',
            'Invalid URL format': 'Please enter a valid URL'
          };
          
          errorMessage = errorMappings[firstError.msg] || firstError.msg;
        } else {
          errorMessage = 'Please check your input and try again';
        }
      } else {
        errorMessage = detail;
      }
      
      switch (status) {
        case 400:
          toast.error(errorMessage || 'Bad request');
          break;
        case 401:
          toast.error('Unauthorized access');
          break;
        case 403:
          toast.error('Access denied');
          break;
        case 404:
          toast.error('Resource not found');
          break;
        case 422:
          toast.error(errorMessage || 'Please check your input and try again');
          break;
        case 429:
          toast.error('Too many requests. Please try again later');
          break;
        case 500:
          toast.error('Server error. Please try again later');
          break;
        default:
          toast.error(errorMessage || `Request failed (${status})`);
      }
    } else if (error.request) {
      toast.error('Unable to connect to server. Please check your internet connection');
    } else {
      toast.error('An unexpected error occurred');
    }
  }
}

// Create and export singleton instance
export const apiService = new ApiService();
export default apiService;
